import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import ProfileView from "@/components/ProfileView";
import { PRESET_BADGES } from "@/components/Badges";
import UsernameEffect, { USERNAME_EFFECTS } from "@/components/UsernameEffect";
import { FONTS, AVATAR_EFFECTS, THEMES, LAYOUTS, fontFamily } from "@/components/profileConfig";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Loader2, Upload, Plus, Trash2, GripVertical, Save, ExternalLink,
  ImageIcon, Palette, Link2, Music, User, Check, Sparkles, Wand2, Type, LayoutGrid, Award,
} from "lucide-react";

const LINK_ICONS = ["link", "github", "twitter", "instagram", "youtube", "twitch", "globe", "music"];
const SHAPES = [
  { id: "circle", label: "Circle" },
  { id: "rounded", label: "Rounded" },
  { id: "squircle", label: "Squircle" },
  { id: "square", label: "Square" },
];
const AUDIO_PRESETS = [
  { name: "Lofi Chill", url: "https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3" },
  { name: "Ambient Dream", url: "https://cdn.pixabay.com/download/audio/2021/11/25/audio_00fa5593f3.mp3" },
];

const Section = ({ title, children }) => (
  <div className="rounded-2xl border border-[#22222B] bg-[#111115] p-5 space-y-4">
    <h3 className="text-sm font-mono uppercase tracking-wider text-zinc-400">{title}</h3>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props} className="w-full bg-[#18181F] border border-[#22222B] focus:border-violet-500/60 rounded-xl px-3.5 py-2.5 text-white text-sm outline-none transition-colors placeholder:text-zinc-600" />
);

function UploadBox({ label, path, accept, onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.path);
      toast.success(`${label} uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const isAudio = accept?.includes("audio");

  return (
    <div>
      <input ref={inputRef} type="file" accept={accept} onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <button
        type="button"
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        className="w-full rounded-xl border border-dashed border-[#2d2d37] hover:border-violet-500/50 bg-[#18181F] p-4 flex flex-col items-center justify-center gap-2 text-center transition-colors group"
      >
        {busy ? (
          <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
        ) : path && !isAudio ? (
          <img src={fileUrl(path)} alt={label} className="w-full h-24 object-cover rounded-lg" />
        ) : (
          <>
            {isAudio ? <Music className="w-5 h-5 text-zinc-500 group-hover:text-violet-400" /> : <Upload className="w-5 h-5 text-zinc-500 group-hover:text-violet-400" />}
            <span className="text-xs text-zinc-500">Drag &amp; drop or <span className="text-violet-400">choose file</span></span>
          </>
        )}
      </button>
      {path && (isAudio || true) && (
        <p className="text-[11px] text-zinc-600 mt-1.5 truncate">{isAudio ? "Audio set ✓" : ""}</p>
      )}
    </div>
  );
}

function CustomBadgeIcon({ badge, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.path);
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid={`custom-badge-upload-${badge.id}`}
        className="shrink-0 w-10 h-10 rounded-lg border border-[#2d2d37] hover:border-violet-500/50 bg-[#111115] flex items-center justify-center overflow-hidden transition-colors"
      >
        {busy ? <Loader2 className="w-4 h-4 text-violet-400 animate-spin" /> : badge.image_path ? <img src={fileUrl(badge.image_path)} alt={badge.label} className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-zinc-500" />}
      </button>
    </>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => ({
    display_name: "", bio: "", avatar_path: "", banner_path: "", background_path: "",
    background_audio_path: "", audio_url: "", avatar_shape: "circle",
    background_color: "#08080A", accent_color: "#8B5CF6", badges: [], links: [], audio_enabled: false, name_effect: "none", avatar_effect: "none", font: "default", theme: "", layout: "classic", custom_badges: [], discord_enabled: false, discord_id: "",
    ...(user?.profile || {}),
  }));
  const [saving, setSaving] = useState(false);
  const [effectsOpen, setEffectsOpen] = useState(false);
  const tokens = user?.custom_badge_tokens || 0;

  const set = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const applyTheme = (t) => {
    set({ theme: t.id, background_color: t.bg, accent_color: t.accent, name_effect: t.name_effect, avatar_effect: t.avatar_effect, font: t.font, layout: t.layout || "classic", background_path: "" });
    toast.success(`${t.label} theme applied`);
  };

  const addCustomBadge = () => {
    if (profile.custom_badges.length >= tokens) {
      toast.error("No custom badge tokens left. Ask an admin to grant more.");
      return;
    }
    set({ custom_badges: [...profile.custom_badges, { id: crypto.randomUUID(), label: "New Badge", image_path: "" }] });
  };
  const updateCustomBadge = (id, patch) => set({ custom_badges: profile.custom_badges.map((b) => (b.id === id ? { ...b, ...patch } : b)) });
  const removeCustomBadge = (id) => set({ custom_badges: profile.custom_badges.filter((b) => b.id !== id) });

  const addLink = () => set({ links: [...profile.links, { id: crypto.randomUUID(), title: "", url: "", icon: "link" }] });
  const updateLink = (id, patch) => set({ links: profile.links.map((l) => (l.id === id ? { ...l, ...patch } : l)) });
  const removeLink = (id) => set({ links: profile.links.filter((l) => l.id !== id) });

  const toggleBadge = (b) =>
    set({ badges: profile.badges.includes(b) ? profile.badges.filter((x) => x !== b) : [...profile.badges, b] });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile/me", profile);
      setUser(data);
      toast.success("Profile saved!");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-28 pb-16 px-4 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-serif font-bold text-3xl text-white">Customize</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Your page is live at{" "}
              <a href={`/${user.username}`} target="_blank" rel="noreferrer" className="text-violet-400 hover:text-violet-300 font-mono" data-testid="view-profile-link">
                grime.bio/{user.username} <ExternalLink className="inline w-3 h-3" />
              </a>
            </p>
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="bg-violet-600 hover:bg-violet-500 text-white rounded-full px-6 shadow-[0_0_20px_rgba(139,92,246,0.4)]">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Editor */}
          <div className="lg:col-span-7">
            <Tabs defaultValue="profile">
              <TabsList className="bg-[#111115] border border-[#22222B] rounded-xl p-1 flex-wrap h-auto">
                <TabsTrigger value="profile" data-testid="tab-profile" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-zinc-400"><User className="w-4 h-4 mr-1.5" />Profile</TabsTrigger>
                <TabsTrigger value="appearance" data-testid="tab-appearance" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-zinc-400"><Palette className="w-4 h-4 mr-1.5" />Appearance</TabsTrigger>
                <TabsTrigger value="themes" data-testid="tab-themes" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-zinc-400"><Wand2 className="w-4 h-4 mr-1.5" />Themes</TabsTrigger>
                <TabsTrigger value="links" data-testid="tab-links" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-zinc-400"><Link2 className="w-4 h-4 mr-1.5" />Links</TabsTrigger>
                <TabsTrigger value="audio" data-testid="tab-audio" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-zinc-400"><Music className="w-4 h-4 mr-1.5" />Audio</TabsTrigger>
              </TabsList>

              {/* PROFILE */}
              <TabsContent value="profile" className="mt-5 space-y-5">
                <Section title="Identity">
                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-500">Display name</label>
                    <Input value={profile.display_name} data-testid="input-display-name" onChange={(e) => set({ display_name: e.target.value })} placeholder={user.username} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-500">Bio</label>
                    <textarea value={profile.bio} data-testid="input-bio" onChange={(e) => set({ bio: e.target.value })} rows={3} placeholder="Tell the world about yourself..." className="w-full bg-[#18181F] border border-[#22222B] focus:border-violet-500/60 rounded-xl px-3.5 py-2.5 text-white text-sm outline-none transition-colors placeholder:text-zinc-600 resize-none" />
                  </div>
                </Section>
                <Section title="Username Effect">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-2xl font-serif font-bold truncate">
                        <UsernameEffect text={profile.display_name || user.username} effect={profile.name_effect} />
                      </div>
                      <p className="text-[11px] text-zinc-600 mt-1 capitalize">
                        {USERNAME_EFFECTS.find((e) => e.id === profile.name_effect)?.label || "None"} effect
                      </p>
                    </div>
                    <Button onClick={() => setEffectsOpen(true)} data-testid="open-effects-btn" className="shrink-0 bg-violet-600 hover:bg-violet-500 text-white rounded-full">
                      <Sparkles className="w-4 h-4 mr-2" /> Choose effect
                    </Button>
                  </div>
                </Section>
                <Section title="Badges">
                  <div className="flex flex-wrap gap-2">
                    {PRESET_BADGES.map((b) => {
                      const active = profile.badges.includes(b);
                      return (
                        <button key={b} data-testid={`badge-toggle-${b.toLowerCase()}`} onClick={() => toggleBadge(b)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? "bg-violet-600 border-violet-500 text-white" : "bg-[#18181F] border-[#22222B] text-zinc-400 hover:border-violet-500/40"}`}>
                          {active && <Check className="w-3 h-3" />} {b}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-zinc-600">Admins can grant exclusive badges to any user from the admin panel.</p>
                </Section>
                <Section title="My Custom Badges">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-500">
                      <span className="text-violet-300 font-medium">{profile.custom_badges.length}/{tokens}</span> tokens used. Tokens are granted by an admin.
                    </p>
                    <Button onClick={addCustomBadge} disabled={profile.custom_badges.length >= tokens} data-testid="add-custom-badge-btn" size="sm" className="bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white rounded-full">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {tokens === 0 && (
                    <div className="rounded-xl border border-dashed border-[#2d2d37] p-4 text-center">
                      <Award className="w-5 h-5 text-zinc-600 mx-auto mb-1" />
                      <p className="text-xs text-zinc-600">You have no custom badge tokens yet. Ask an admin to grant you some.</p>
                    </div>
                  )}
                  <div className="space-y-3">
                    {profile.custom_badges.map((b) => (
                      <div key={b.id} data-testid={`custom-badge-row-${b.id}`} className="rounded-xl border border-[#22222B] bg-[#18181F] p-3 flex items-center gap-3">
                        <CustomBadgeIcon badge={b} onUploaded={(path) => updateCustomBadge(b.id, { image_path: path })} />
                        <Input value={b.label} data-testid={`custom-badge-label-${b.id}`} onChange={(e) => updateCustomBadge(b.id, { label: e.target.value })} placeholder="Badge name" />
                        <button onClick={() => removeCustomBadge(b.id)} data-testid={`custom-badge-remove-${b.id}`} className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </Section>
                <Section title="Discord Presence">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-500">Show your live Discord status on your profile.</p>
                    <button
                      onClick={() => set({ discord_enabled: !profile.discord_enabled })}
                      data-testid="discord-toggle"
                      className={`relative w-11 h-6 rounded-full transition-colors ${profile.discord_enabled ? "bg-violet-600" : "bg-[#2d2d37]"}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${profile.discord_enabled ? "translate-x-5" : ""}`} />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-500">Discord User ID</label>
                    <Input value={profile.discord_id} data-testid="input-discord-id" onChange={(e) => set({ discord_id: e.target.value.replace(/[^0-9]/g, "") })} placeholder="e.g. 123456789012345678" />
                    <p className="text-[11px] text-zinc-600">
                      Join <a href="https://discord.gg/lanyard" target="_blank" rel="noreferrer" className="text-violet-400 hover:text-violet-300">discord.gg/lanyard</a> and paste your Discord user ID so we can read your live presence.
                    </p>
                  </div>
                </Section>
              </TabsContent>

              {/* APPEARANCE */}
              <TabsContent value="appearance" className="mt-5 space-y-5">
                <Section title="Avatar">
                  <UploadBox label="Avatar" path={profile.avatar_path} accept="image/*" testid="upload-avatar" onUploaded={(p) => set({ avatar_path: p })} />
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Shape</label>
                    <div className="grid grid-cols-4 gap-2">
                      {SHAPES.map((s) => (
                        <button key={s.id} data-testid={`shape-${s.id}`} onClick={() => set({ avatar_shape: s.id })} className={`py-2 rounded-lg text-xs border transition-all ${profile.avatar_shape === s.id ? "bg-violet-600/20 border-violet-500 text-violet-300" : "bg-[#18181F] border-[#22222B] text-zinc-400"}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Avatar effect</label>
                    <div className="grid grid-cols-3 gap-2">
                      {AVATAR_EFFECTS.map((e) => (
                        <button key={e.id} data-testid={`avatar-effect-${e.id}`} onClick={() => set({ avatar_effect: e.id })} className={`py-2 rounded-lg text-xs border transition-all ${profile.avatar_effect === e.id ? "bg-violet-600/20 border-violet-500 text-violet-300" : "bg-[#18181F] border-[#22222B] text-zinc-400 hover:border-violet-500/40"}`}>
                          {e.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </Section>
                <Section title="Font">
                  <p className="text-xs text-zinc-500 -mt-1">Includes Minecraft, Fortnite and more.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {FONTS.map((f) => {
                      const ff = fontFamily(f.id);
                      const active = profile.font === f.id;
                      return (
                        <button key={f.id} data-testid={`font-${f.id}`} onClick={() => set({ font: f.id })} className={`py-3 px-2 rounded-lg border transition-all flex flex-col items-center gap-1 ${active ? "bg-violet-600/20 border-violet-500" : "bg-[#18181F] border-[#22222B] hover:border-violet-500/40"}`}>
                          <span className="text-lg leading-none text-white" style={ff ? { fontFamily: ff } : undefined}>Ag</span>
                          <span className="text-[10px] text-zinc-400">{f.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </Section>
                <Section title="Profile Layout">
                  <div className="grid grid-cols-2 gap-2">
                    {LAYOUTS.map((l) => (
                      <button key={l.id} data-testid={`layout-${l.id}`} onClick={() => set({ layout: l.id })} className={`p-3 rounded-xl border text-left transition-all ${profile.layout === l.id ? "bg-violet-600/20 border-violet-500" : "bg-[#18181F] border-[#22222B] hover:border-violet-500/40"}`}>
                        <div className="flex items-center gap-2 text-sm text-white">
                          <LayoutGrid className="w-4 h-4 text-violet-400" /> {l.label}
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1">{l.desc}</p>
                      </button>
                    ))}
                  </div>
                </Section>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Section title="Background Image"><UploadBox label="Background" path={profile.background_path} accept="image/*" testid="upload-background" onUploaded={(p) => set({ background_path: p })} /></Section>
                  <Section title="Banner Image"><UploadBox label="Banner" path={profile.banner_path} accept="image/*" testid="upload-banner" onUploaded={(p) => set({ banner_path: p })} /></Section>
                </div>
                <Section title="Colors">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-zinc-500 mb-2 block">Background color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={profile.background_color} data-testid="color-background" onChange={(e) => set({ background_color: e.target.value })} className="w-10 h-10 rounded-lg bg-transparent border border-[#22222B] cursor-pointer" />
                        <span className="font-mono text-xs text-zinc-400">{profile.background_color}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 mb-2 block">Accent color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={profile.accent_color} data-testid="color-accent" onChange={(e) => set({ accent_color: e.target.value })} className="w-10 h-10 rounded-lg bg-transparent border border-[#22222B] cursor-pointer" />
                        <span className="font-mono text-xs text-zinc-400">{profile.accent_color}</span>
                      </div>
                    </div>
                  </div>
                  {profile.background_path && (
                    <button onClick={() => set({ background_path: "" })} className="text-xs text-rose-400 hover:text-rose-300" data-testid="clear-background">Remove background image</button>
                  )}
                </Section>
              </TabsContent>

              {/* THEMES */}
              <TabsContent value="themes" className="mt-5 space-y-5">
                <Section title="One-Tap Themes">
                  <p className="text-xs text-zinc-500 -mt-1">Each theme restyles your whole page — colors, font, name effect and avatar effect. Fine-tune anything afterward in Appearance.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {THEMES.map((t) => {
                      const active = profile.theme === t.id;
                      return (
                        <button
                          key={t.id}
                          data-testid={`theme-${t.id}`}
                          onClick={() => applyTheme(t)}
                          className={`relative rounded-xl border p-4 text-left overflow-hidden transition-all ${active ? "border-violet-500 ring-2 ring-violet-500/40" : "border-[#22222B] hover:border-violet-500/40"}`}
                          style={{ background: t.bg }}
                        >
                          {active && <Check className="absolute top-2 right-2 w-4 h-4 text-violet-300" />}
                          <div className="text-xl font-bold" style={{ fontFamily: fontFamily(t.font) || undefined }}>
                            <UsernameEffect text={profile.display_name || user.username} effect={t.name_effect} />
                          </div>
                          <div className="flex items-center gap-1.5 mt-3">
                            {t.swatch.map((c, i) => (
                              <span key={i} className="w-4 h-4 rounded-full border border-white/20" style={{ background: c }} />
                            ))}
                            <span className="text-[11px] text-zinc-400 ml-1">{t.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Section>
              </TabsContent>
              <TabsContent value="links" className="mt-5 space-y-5">
                <Section title="Your Links">
                  <div className="space-y-3">
                    {profile.links.length === 0 && <p className="text-sm text-zinc-600">No links yet. Add your first one below.</p>}
                    {profile.links.map((l) => (
                      <div key={l.id} data-testid={`link-row-${l.id}`} className="rounded-xl border border-[#22222B] bg-[#18181F] p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-zinc-600 shrink-0" />
                          <Input value={l.title} data-testid={`link-title-${l.id}`} onChange={(e) => updateLink(l.id, { title: e.target.value })} placeholder="Title (e.g. Twitter)" />
                          <button onClick={() => removeLink(l.id)} data-testid={`link-remove-${l.id}`} className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <Input value={l.url} data-testid={`link-url-${l.id}`} onChange={(e) => updateLink(l.id, { url: e.target.value })} placeholder="https://..." />
                        <div className="flex flex-wrap gap-1.5">
                          {LINK_ICONS.map((ic) => (
                            <button key={ic} data-testid={`link-icon-${l.id}-${ic}`} onClick={() => updateLink(l.id, { icon: ic })} className={`px-2.5 py-1 rounded-md text-[11px] capitalize border transition-all ${l.icon === ic ? "bg-violet-600/20 border-violet-500 text-violet-300" : "bg-[#111115] border-[#22222B] text-zinc-500"}`}>
                              {ic}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={addLink} data-testid="add-link-btn" className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#2d2d37] hover:border-violet-500/50 py-3 text-sm text-zinc-400 hover:text-violet-300 transition-colors">
                    <Plus className="w-4 h-4" /> Add link
                  </button>
                </Section>
              </TabsContent>

              {/* AUDIO */}
              <TabsContent value="audio" className="mt-5 space-y-5">
                <Section title="Background Music">
                  <p className="text-xs text-zinc-500">Pick a preset, paste an audio URL, or upload your own track.</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {AUDIO_PRESETS.map((a) => (
                      <button key={a.name} data-testid={`audio-preset-${a.name.toLowerCase().replace(/\s/g, "-")}`} onClick={() => set({ audio_url: a.url, background_audio_path: "" })} className={`px-3 py-2.5 rounded-xl text-sm border transition-all text-left ${profile.audio_url === a.url ? "bg-violet-600/20 border-violet-500 text-violet-200" : "bg-[#18181F] border-[#22222B] text-zinc-300"}`}>
                        <Music className="w-4 h-4 inline mr-2" /> {a.name}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-500">Custom audio URL</label>
                    <Input value={profile.audio_url} data-testid="input-audio-url" onChange={(e) => set({ audio_url: e.target.value, background_audio_path: "" })} placeholder="https://...mp3" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-500">Or upload a file</label>
                    <UploadBox label="Audio" path={profile.background_audio_path} accept="audio/*" testid="upload-audio" onUploaded={(p) => set({ background_audio_path: p, audio_url: "" })} />
                  </div>
                  {(profile.audio_url || profile.background_audio_path) && (
                    <button onClick={() => set({ audio_url: "", background_audio_path: "" })} className="text-xs text-rose-400 hover:text-rose-300" data-testid="clear-audio">Remove audio</button>
                  )}
                </Section>
              </TabsContent>
            </Tabs>
          </div>

          {/* Live preview */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3 text-center">Live Preview</p>
              <div className="mx-auto max-w-sm rounded-[2rem] border-4 border-[#1a1a20] bg-black overflow-hidden shadow-2xl" style={{ height: "600px" }}>
                <ProfileView profile={profile} username={user.username} views={user.views} preview />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={effectsOpen} onOpenChange={setEffectsOpen}>
        <DialogContent className="bg-[#111115] border-[#22222B] text-white max-w-2xl" data-testid="effects-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Username Effects</DialogTitle>
            <DialogDescription className="text-zinc-500">Pick an effect — each preview shows your own name.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto py-1 pr-1">
            {USERNAME_EFFECTS.map((e) => {
              const active = profile.name_effect === e.id;
              return (
                <button
                  key={e.id}
                  data-testid={`effect-${e.id}`}
                  onClick={() => set({ name_effect: e.id })}
                  className={`relative rounded-xl border p-4 h-24 flex flex-col items-center justify-center gap-1.5 transition-all overflow-hidden ${active ? "border-violet-500 bg-violet-600/10 ring-2 ring-violet-500/40" : "border-[#22222B] bg-[#18181F] hover:border-violet-500/40"}`}
                >
                  {active && <Check className="absolute top-2 right-2 w-4 h-4 text-violet-400" />}
                  <span className="text-lg font-serif font-bold truncate max-w-full">
                    <UsernameEffect text={profile.display_name || user.username} effect={e.id} />
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{e.label}</span>
                </button>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setEffectsOpen(false)} data-testid="effects-cancel-btn" className="rounded-full border border-[#22222B] text-zinc-300 hover:bg-white/5 hover:text-white">
              Cancel
            </Button>
            <Button
              onClick={async () => { setEffectsOpen(false); await save(); }}
              data-testid="effects-save-btn"
              className="bg-violet-600 hover:bg-violet-500 text-white rounded-full px-8 shadow-[0_0_20px_rgba(139,92,246,0.4)]"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
